 
import { Routes, Route, Link } from 'react-router-dom';
import { Suspense } from 'react';
import { lazyWithReload } from './lib/lazyWithReload.js';
import { AgentProvider } from './store/agentStore.jsx';
import { AuthProvider, useAuth, getImpersonationHint } from './context/AuthContext.jsx';
import LandingPage from './pages/LandingPage.jsx';
import { isApexHost } from './lib/orgSlug.js';
import AppShell from './components/layout/AppShell.jsx';
import ErrorBoundary from './components/shared/ErrorBoundary.jsx';
import RequireAuth from './components/shared/RequireAuth.jsx';
import Overview from './pages/Overview.jsx';
import Devices from './pages/Devices.jsx';
import Alerts from './pages/Alerts.jsx';
import Handoff from './pages/Handoff.jsx';
import { SignInRequiredPanel } from './components/auth/SignInRequiredPanel.jsx';
// Lazy so the showcase never rides along even if future refactoring
// introduces a side-effectful top-level import that defeats the
// tree-shaker's ability to elide a statically-imported module.
const ComponentsShowcase = lazyWithReload(() => import('./pages/ComponentsShowcase.jsx'));

// Lazy-load pages with heavy dependencies (Recharts, ReactFlow)
const Topology = lazyWithReload(() => import('./pages/Topology.jsx'));
const Uplinks = lazyWithReload(() => import('./pages/Uplinks.jsx'));
const Hosts = lazyWithReload(() => import('./pages/Hosts.jsx'));
const HostDetail = lazyWithReload(() => import('./pages/HostDetail.jsx'));
const DeviceDetail = lazyWithReload(() => import('./pages/DeviceDetail.jsx'));
const Subscribers = lazyWithReload(() => import('./pages/Subscribers.jsx'));
const SubscriberDetail = lazyWithReload(() => import('./pages/SubscriberDetail.jsx'));
const Flows = lazyWithReload(() => import('./pages/Flows.jsx'));
const Logs = lazyWithReload(() => import('./pages/Logs.jsx'));
const EventSources = lazyWithReload(() => import('./pages/EventSources.jsx'));
const Settings = lazyWithReload(() => import('./pages/Settings.jsx'));
const ProxyDetail = lazyWithReload(() => import('./pages/ProxyDetail.jsx'));
const ProxyMacTable = lazyWithReload(() => import('./pages/ProxyMacTable.jsx'));
const AgentDetail = lazyWithReload(() => import('./pages/AgentDetail.jsx'));
const UserManagement = lazyWithReload(() => import('./pages/UserManagement.jsx'));
const Services = lazyWithReload(() => import('./pages/Services.jsx'));
const ServiceDetail = lazyWithReload(() => import('./pages/ServiceDetail.jsx'));

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
    </div>
  );
}
 

function AuthGate({ children }) {
  const { loading, isAuthenticated, error, errorType, refetch } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="mt-4 text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (errorType === 'admin_console_required') {
      // #791: a vista_admin who was impersonating and did a full-page reload
      // lands here (the memory-only impersonation token is gone). Use the
      // non-secret breadcrumb to explain what happened + offer a resume path,
      // instead of the bare "use the Admin Console" message that leaves a
      // fat-fingered refresh confusing.
      const hint = getImpersonationHint();
      if (hint?.org_id) {
        const resumeUrl = `https://admin.vista-cloud.io/orgs/${encodeURIComponent(hint.org_id)}`;
        return (
          <div className="flex h-screen items-center justify-center bg-gray-50">
            <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
              <h1 className="text-xl font-bold text-gray-900">Impersonation session ended</h1>
              <p className="mt-2 text-sm text-gray-600">
                Your impersonation session for{' '}
                <span className="font-medium text-gray-900">{hint.org_name || 'this organization'}</span>{' '}
                ended when the page reloaded (the session is held in memory only and
                can&apos;t survive a refresh or direct link). Return to the Admin
                Console to re-open it.
              </p>
              <a
                href={resumeUrl}
                className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Resume in Admin Console
              </a>
            </div>
          </div>
        );
      }
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">VISTA Operator Dashboard</h1>
            <p className="mt-2 text-sm text-gray-600">
              This is the VISTA Operator Dashboard. VISTA Admin accounts must use the{' '}
              <a href="https://admin.vista-cloud.io" className="font-medium text-blue-600 hover:text-blue-800">Admin Console</a>.
            </p>
          </div>
        </div>
      );
    }

    if (errorType === 'auth_failed') {
      return <SignInRequiredPanel />;
    }

    if (errorType === 'org_mismatch') {
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">Wrong Organization</h1>
            <p className="mt-2 text-sm text-gray-600">
              Your account does not have access to this organization. Please check the URL or contact your administrator.
            </p>
          </div>
        </div>
      );
    }

    if (errorType === 'invitation_expired') {
      // #231: distinct from "not_provisioned" — the operator who sent the
      // invitation can resolve this by clicking "Resend" on the invitation
      // in the User Management view (resets expires_at to NOW + 7d).
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">Invitation Expired</h1>
            <p className="mt-2 text-sm text-gray-600">
              Your invitation to VISTA has expired. Please contact your
              administrator to resend the invitation — they can do this
              from the User Management page without revoking your access.
            </p>
          </div>
        </div>
      );
    }

    if (errorType === 'not_provisioned') {
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">Access Not Provisioned</h1>
            <p className="mt-2 text-sm text-gray-600">
              Your account has not been provisioned for VISTA. Please contact your administrator.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <h1 className="text-xl font-bold text-gray-900">Unable to Connect</h1>
          <p className="mt-2 text-sm text-gray-600">
            {error || 'Could not reach the VISTA API. Please try again later.'}
          </p>
          <button
            onClick={refetch}
            className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return children;
}

// #790: nested catch-all inside AppShell. Without this, an unmatched path
// renders the shell with an empty <Outlet/> — a blank white content area.
// Rendered within the shell (sidebar/header stay), so it's page content only.
function NotFound() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <p className="text-5xl font-bold text-gray-300">404</p>
      <h1 className="mt-3 text-lg font-semibold text-gray-900">Page not found</h1>
      <p className="mt-1 text-sm text-gray-500">
        The page you&apos;re looking for doesn&apos;t exist or has moved.
      </p>
      <Link
        to="/"
        className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
      >
        Back to Overview
      </Link>
    </div>
  );
}

export function AuthenticatedApp() {
  return (
    <AuthGate>
      <AgentProvider>
        <Routes>
          <Route element={<AppShell />}>
            <Route path="/" element={<ErrorBoundary><Overview /></ErrorBoundary>} />
            <Route path="/topology" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><Topology /></Suspense></ErrorBoundary>} />
            <Route path="/uplinks" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><Uplinks /></Suspense></ErrorBoundary>} />
            <Route path="/hosts" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><Hosts /></Suspense></ErrorBoundary>} />
            <Route path="/hosts/:hostId" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><HostDetail /></Suspense></ErrorBoundary>} />
            <Route path="/devices" element={<ErrorBoundary><Devices /></ErrorBoundary>} />
            <Route path="/devices/:id" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><DeviceDetail /></Suspense></ErrorBoundary>} />
            <Route path="/subscribers" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><Subscribers /></Suspense></ErrorBoundary>} />
            <Route path="/subscribers/:accountId" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><SubscriberDetail /></Suspense></ErrorBoundary>} />
            <Route path="/flows" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><Flows /></Suspense></ErrorBoundary>} />
            <Route path="/logs" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><Logs /></Suspense></ErrorBoundary>} />
            <Route path="/services" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><Services /></Suspense></ErrorBoundary>} />
            <Route path="/services/:id" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><ServiceDetail /></Suspense></ErrorBoundary>} />
            <Route path="/alerts" element={<ErrorBoundary><Alerts /></ErrorBoundary>} />
            <Route path="/alerts/rules" element={<ErrorBoundary><Alerts /></ErrorBoundary>} />
            {/* vista#956: NOT a Settings tab. `/settings` is wrapped in
                `RequireAuth role="org_admin"` and the header hides the gear
                below that level, so a Settings tab is unreachable for
                `org_ro` no matter what `requiredRole` the tab carries — the
                first version of this shipped a read-only branch that was dead
                in the product (#978 review R1). The whole argument for this
                page is that the person who notices a device missing from
                their logs is a viewer, so it is routed like /logs: reachable
                by anyone, with the decide controls gated inside. */}
            <Route path="/event-sources" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><EventSources /></Suspense></ErrorBoundary>} />
            <Route path="/settings" element={<ErrorBoundary><RequireAuth role="org_admin"><Suspense fallback={<PageFallback />}><Settings /></Suspense></RequireAuth></ErrorBoundary>} />
            <Route path="/settings/proxies/:proxyId" element={<ErrorBoundary><RequireAuth role="org_admin"><Suspense fallback={<PageFallback />}><ProxyDetail /></Suspense></RequireAuth></ErrorBoundary>} />
            <Route path="/settings/proxies/:proxyId/mac-table" element={<ErrorBoundary><RequireAuth role="org_admin"><Suspense fallback={<PageFallback />}><ProxyMacTable /></Suspense></RequireAuth></ErrorBoundary>} />
            <Route path="/agents/:agentId" element={<ErrorBoundary><Suspense fallback={<PageFallback />}><AgentDetail /></Suspense></ErrorBoundary>} />
            <Route path="/users" element={<ErrorBoundary><RequireAuth role="org_admin"><Suspense fallback={<PageFallback />}><UserManagement /></Suspense></RequireAuth></ErrorBoundary>} />
            {/* #790: catch-all so unknown paths show a 404, not a blank outlet. */}
            <Route path="*" element={<ErrorBoundary><NotFound /></ErrorBoundary>} />
          </Route>
        </Routes>
      </AgentProvider>
    </AuthGate>
  );
}

export default function App() {
  // Short-circuit at the apex: vista-cloud.io is NOT behind CF Access
  // (per-org subdomains are), so running the normal auth flow here
  // would loop against 401 from api.vista-cloud.io. Instead, serve a
  // public landing page that redirects to the user's org subdomain.
  // See src/pages/LandingPage.jsx + vista#... for rationale.
  if (isApexHost()) {
    return <LandingPage />;
  }

  // S11-5 dev-only component showcase — never reachable in a
  // production build. Bypasses AuthProvider so reviewers can load it
  // without a session. Suspense wrapper is here because the component
  // is lazy-loaded (see import) — in dev the resolver is instant so
  // the spinner almost never renders, but it's needed for correctness.
  if (import.meta.env.DEV && typeof window !== 'undefined' && window.location.pathname === '/__components') {
    return (
      <Suspense fallback={<PageFallback />}>
        <ComponentsShowcase />
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <Routes>
        <Route path="/handoff" element={<Handoff />} />
        <Route path="*" element={<AuthenticatedApp />} />
      </Routes>
    </AuthProvider>
  );
}
